import { useEffect, useMemo, useState } from 'react'
import { Loader2, ScanLine } from 'lucide-react'
import type { UseChecklistScan } from '@/screens/scan/useChecklistScan'
import type { ScanSession } from '@/screens/scan/types'
import { SessionCard } from './SessionCard'
import { SessionDetailView } from './SessionDetailView'

/**
 * Initial scan view: the open document's scan history, grouped by run (newest
 * first), with a Scan button that opens the checklist-item picker drawer. New
 * runs appear here as a pending card and resolve in place.
 */
export function HistoryView({ scan }: { scan: UseChecklistScan }) {
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null)
  const itemsById = useMemo(() => new Map(scan.items.map((item) => [item._id, item])), [scan.items])

  const sessions = useMemo<ScanSession[]>(() => {
    const byScan = new Map<string, ScanSession>()
    for (const row of scan.results) {
      const scanId = row.checklistItemScanId ?? row._id ?? 'unknown'
      const existing = byScan.get(scanId)
      if (existing) {
        existing.results.push(row)
        if (row.createdAt && (!existing.createdAt || row.createdAt > existing.createdAt)) {
          existing.createdAt = row.createdAt
        }
      } else {
        byScan.set(scanId, { scanId, createdAt: row.createdAt, results: [row] })
      }
    }
    return [...byScan.values()].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
  }, [scan.results])

  const pendingScanId = useMemo(
    () => sessions.find((session) => session.results.some((row) => row.status === 'INITIAL'))?.scanId,
    [sessions]
  )

  const selectedSession = useMemo(
    () => (selectedSessionId ? sessions.find((session) => session.scanId === selectedSessionId) : undefined),
    [selectedSessionId, sessions]
  )

  useEffect(() => {
    if (selectedSessionId && !selectedSession) setSelectedSessionId(null)
  }, [selectedSessionId, selectedSession])

  if (selectedSession) {
    return (
      <SessionDetailView
        session={selectedSession}
        itemsById={itemsById}
        current={selectedSession.scanId === pendingScanId}
        onBack={() => setSelectedSessionId(null)}
        onRescanItems={scan.rescanItems}
        onLoadHighlights={scan.ensureHighlights}
      />
    )
  }

  return (
    <>
      <div className='ramp-scan-head'>
        <div className='ramp-scan-title'>Scan results</div>
      </div>

      <div className='ramp-scan-list'>
        {scan.loadingHistory && sessions.length === 0 ? (
          <div className='ramp-scan-empty'>
            <Loader2 size={16} className='ramp-spin' /> Loading scan history…
          </div>
        ) : scan.historyError ? (
          <div className='ramp-scan-empty ramp-scan-error'>
            {scan.historyError}
            <button type='button' className='ramp-link' onClick={scan.reloadHistory}>
              Retry
            </button>
          </div>
        ) : sessions.length === 0 ? (
          <div className='ramp-scan-empty'>No scans yet for this document. Run a scan to get started.</div>
        ) : (
          sessions.map((session) => (
            <SessionCard
              key={session.scanId}
              session={session}
              current={session.scanId === pendingScanId}
              onSelect={() => setSelectedSessionId(session.scanId)}
            />
          ))
        )}
      </div>

      <div className='ramp-scan-foot'>
        <button type='button' className='ramp-scan-primary' onClick={scan.openDrawer}>
          <ScanLine size={16} /> Scan
        </button>
      </div>
    </>
  )
}
